import { HSpacer, Text, VSpacer } from '@/components/DesignSystem';
import { ColorIndex } from '@/constants/ChartColorIndex';
import { QueryKeys } from '@/constants/QueryKeys';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { ProgramPeriodSelector } from '@/pages/RetailerOverview/ProgramPeriodSelector';
import { DashboardApi } from "@/utilities/api/DashboardApi";
import { UserApi } from '@/utilities/api/UserApi';
import { ApiCategory, ApiUser } from '@api/interfaces';
import { ApiFarmerRewards } from '@api/interfaces/ApiFarmerRewards';
import { ApiRewardsProgram } from '@api/interfaces/ApiRewardsProgram';
import { Box, Divider, Skeleton, Stack } from '@mui/material';
import { AccountGroupRole, CIBPageName } from '@shared/enums';
import { ProgramStatus } from '@shared/enums/ProgramStatus';
import {
  CategoryReward,
  formatCurrency,
  formatShortCurrency,
  getProgramStatus,
  shouldHideZeroDollarTotalSpendForCategory,
} from '@shared/utilities';
import { EChartsOption } from 'echarts';
import ReactECharts from 'echarts-for-react';
import i18next from 'i18next';
import React, { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import { AboutAccountRolesBanner } from '../LinkedAccounts';

interface SpendingBreakdownProps {
  accountGroupRole: AccountGroupRole | null,
  activeProgram: ApiRewardsProgram,
  activeFarmerRewards: ApiFarmerRewards,
  brandColor: string,
  categories: ApiCategory[],
  customerId: string,
  farmerRewards: ApiFarmerRewards[],
  linkedAccounts: ApiUser[],
  programs: ApiRewardsProgram[],
}

const getFilteredAndSortedPrograms = (programs: ApiRewardsProgram[]): ApiRewardsProgram[] => {
  const currentDate = new Date();

  const filteredPrograms = programs.filter((program) => {
    const programStartDate = new Date(program.startDate);
    return programStartDate <= currentDate;
  });

  // Sort programs: Active program first, then past programs newest to oldest
  return filteredPrograms.sort((a, b) => {
    const statusA = getProgramStatus(a);
    const statusB = getProgramStatus(b);

    if (statusA === ProgramStatus.Active && statusB !== ProgramStatus.Active) {
      return -1;
    }
    if (statusB === ProgramStatus.Active && statusA !== ProgramStatus.Active) {
      return 1;
    }

    const startDateA = new Date(a.startDate).getTime();
    const startDateB = new Date(b.startDate).getTime();
    return startDateB - startDateA;
  });
};

export const SpendingBreakdown = ({
  accountGroupRole,
  activeFarmerRewards,
  activeProgram,
  brandColor,
  categories,
  customerId,
  farmerRewards,
  linkedAccounts,
  programs,
}: SpendingBreakdownProps) => {
  const { t } = useTranslation();
  const { isMobile } = useMediaQuery();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const accessToken = searchParams.get('accessToken') ?? params.accessToken;
  const [selectedRewardsProgram, setSelectedRewardsProgram] = useState<ApiRewardsProgram>(
    activeProgram,
  );
  const [selectedFarmerRewards, setSelectedFarmerRewards] = useState<ApiFarmerRewards>(
    activeFarmerRewards,
  );

  const processedPrograms = useMemo(
    () => getFilteredAndSortedPrograms(programs),
    [programs],
  );

  const { data: pointsKpisResponse } = useQuery(
    [QueryKeys.GET_POINTS_KPIS_BY_CUSTOMER, accessToken, selectedRewardsProgram.id],
    () => DashboardApi.getPointsKPIs(accessToken!, selectedRewardsProgram.id),
    { enabled: !!accessToken },
  );
  const pointsKpis = pointsKpisResponse?.data.find(
    (kpi) => kpi.customer.rewardsAccountId === activeFarmerRewards.rewardsAccountId,
  );

  const isIndependentPartner = accountGroupRole === AccountGroupRole.IndependentPartner;

  const { data: individualFarmerRewards } = useQuery({
    queryKey: [QueryKeys.GET_FARMER_REWARDS_FOR_DASHBOARD, accessToken, selectedRewardsProgram.id],
    queryFn: () => DashboardApi.getFarmerRewards(accessToken!, selectedRewardsProgram.id, true),
    enabled: isIndependentPartner && !!accessToken,
  });

  const effectiveFarmerRewards = isIndependentPartner && individualFarmerRewards
    ? individualFarmerRewards
    : selectedFarmerRewards;

  const { data: programPoints, isFetching: areProgramPointsFetching } = useQuery({
    queryKey: [
      QueryKeys.LIST_POINTS_FOR_PROGRAM_PERIOD, customerId, selectedRewardsProgram.id, accessToken,
      isIndependentPartner,
    ],
    queryFn: async () => (
      UserApi.listPointsForProgramPeriod(
        customerId,
        selectedRewardsProgram.id,
        accessToken,
        isIndependentPartner || undefined,
      )
    ),
  });

  const categoryRewards: CategoryReward[] = selectedRewardsProgram.tiers?.[0].segments?.map(
    (segment) => {
      const categoryName = categories.find(
        (category) => category.id === segment.categoryId,
      )?.name ?? '';
      const segmentTotal = effectiveFarmerRewards.totalPerSegment[segment.categoryId];
      return {
        id: segment.categoryId,
        name: categoryName,
        points: programPoints?.categories[segment.categoryId] ?? 0,
        totalSpend: segmentTotal?.totalSpend ?? 0,
      };
    },
  ) ?? [];
  categoryRewards.sort((a, b) => a.name.localeCompare(b.name));

  const totalSpendValues = categoryRewards.map((rewards) => rewards.totalSpend);

  const grantedPoints = programPoints?.grantedPoints ?? 0;

  const totalSpend = effectiveFarmerRewards.totalSpend;

  const chartCategoryData = categoryRewards.filter(category => category.totalSpend > 0);

  const chartData = chartCategoryData.map((c, i) => ({
    value: c.totalSpend,
    name: c.name,
    itemStyle: { color: ColorIndex[i] },
  }));

  const chartOptions: EChartsOption = {
    tooltip: {
      show: true,
      formatter: (params: any) => {
        const value = formatCurrency(params.value, i18next);
        return `<div style="font-weight: bold;">${params.name}</div><div>${value}</div>`;
      },
    },
    legend: {
      orient: 'vertical',
      left: isMobile ? '55%' : '50%',
      top: 'middle',
      itemWidth: 10,
      itemHeight: 10,
      itemGap: 8,
      textStyle: {
        fontSize: 13,
        fontWeight: 'normal',
        color: '#333333',
        overflow: 'break',
        width: isMobile ? 115 : 180,
      },
      icon: 'rect',
      align: 'left',
    },
    series: [
      {
        name: t(($) => $.customerDashboard.spendingBreakdown.totalSpend),
        type: 'pie',
        radius: ['65%', '85%'],
        center: ['28%', '50%'],
        avoidLabelOverlap: false,
        label: { show: false },
        labelLine: { show: false },
        emphasis: {
          scale: false,
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.2)',
          },
        },
        data: chartData,
      },
      {
        type: 'pie',
        radius: ['0%', '40%'],
        center: ['28%', '50%'],
        label: {
          position: 'center',
          formatter: `{value|${formatShortCurrency(totalSpend, i18next)}}\n{label|${t(($) => $.customerDashboard.spendingBreakdown.totalSpendLabel)}}`,
          rich: {
            value: {
              fontSize: 28,
              fontWeight: 'bold',
              color: '#141414',
            },
            label: {
              fontSize: 11,
              color: '#141414',
              lineHeight: 18,
              fontWeight: 'bold',
            },
          },
        },
        data: [{ value: 1 }],
        silent: true,
        itemStyle: { color: 'transparent' },
      },
    ],
  };

  return (
    <Stack px="16px">
      {!!accountGroupRole && !!linkedAccounts?.length && (
        <AboutAccountRolesBanner
          brandColor={brandColor}
          customerRole={accountGroupRole}
          linkedAccounts={linkedAccounts}
          pageName={CIBPageName.CRD_CurrentProgress_SpendingBreakdown}
          showMobileVersion={true}
        />
      )}
      {processedPrograms.length > 1 && (
        <>
          <VSpacer size="4" />
          <ProgramPeriodSelector
            isAlreadySortedPrograms={true}
            onSelectProgram={(program) => {
              setSelectedRewardsProgram(program);
              setSelectedFarmerRewards(farmerRewards.find(
                (reward) => reward.programId === program.id)!,
              );
            }}
            programs={processedPrograms}
            selectedProgram={selectedRewardsProgram}
          />
        </>
      )}
      <VSpacer size="5" />
      <Stack
         border="1px solid rgba(0, 0, 0, 0.04)"
         borderRadius="8px"
         boxShadow="0 1px 2px 0 rgba(0, 0, 0, 0.30), 0 1px 3px 1px rgba(0, 0, 0, 0.15)"
      >
        <Stack alignItems="center" direction="row" justifyContent="space-between">
          {effectiveFarmerRewards.totalSpend === 0 || !totalSpendValues.length ? (
            <Stack>
              <Text category="display-small">
                {formatShortCurrency(effectiveFarmerRewards.totalSpend, i18next)}
              </Text>
              <Text category="overline">
                {t($ => $.customers.details.currentTotalSpend)}
              </Text>
            </Stack>
          ) : (
            <ReactECharts option={chartOptions} style={{ height: '200px', width: '100%' }} />
          )}
        </Stack>
      </Stack>
      <VSpacer size="5" />
      {!!pointsKpis && (
        <>
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Stack
              alignItems="center"
              bgcolor="rgba(255, 255, 255, 0.04)"
              borderRadius="8px"
              boxShadow="0 1px 2px 0 rgba(0, 0, 0, 0.30), 0 1px 3px 1px rgba(0, 0, 0, 0.15)"
              flex={1}
              justifyContent="center"
              p="10px 12px"
            >
              <Text category="label-medium" sx={{ color: '#141414' }}>
                {t($ => $.common.pointStatus.earned)}
              </Text>
              <Text category="title-medium" sx={{ color: '#141414' }}>
                {t($ => $.common.units.shortPts, { count: pointsKpis.pointsIssued })}
              </Text>
            </Stack>

            <Stack
              alignItems="center"
              bgcolor="rgba(255, 255, 255, 0.04)"
              borderRadius="8px"
              boxShadow="0 1px 2px 0 rgba(0, 0, 0, 0.30), 0 1px 3px 1px rgba(0, 0, 0, 0.15)"
              flex={1}
              justifyContent="center"
              p="10px 12px"
            >
              <Text category="label-medium" sx={{ color: '#141414' }}>
                {t($ => $.common.pointStatus.redeemed)}
              </Text>
              <Text category="title-medium" sx={{ color: '#141414' }}>
                {t($ => $.common.units.shortPts, { count: pointsKpis.pointsRedeemed })}
              </Text>
            </Stack>

            <Stack
              alignItems="center"
              bgcolor="rgba(255, 255, 255, 0.04)"
              borderRadius="8px"
              boxShadow="0 1px 2px 0 rgba(0, 0, 0, 0.30), 0 1px 3px 1px rgba(0, 0, 0, 0.15)"
              flex={1}
              justifyContent="center"
              p="10px 12px"
            >
              <Text category="label-medium" sx={{ color: '#D70D09' }}>
                {t($ => $.common.pointStatus.expired)}
              </Text>
              <Text category="title-medium" sx={{ color: '#D70D09' }}>
                {t($ => $.common.units.shortPts, { count: pointsKpis.expiredPoints })}
              </Text>
            </Stack>
          </Stack>
          <VSpacer size="5" />
        </>
      )}
      <VSpacer size="3" />
      <Stack alignItems="center" direction="row" justifyContent="space-between">
        <Text category="title-small">
          {t(($) => $.customerDashboard.spendingBreakdown.categories)}
        </Text>
        <Text category="title-small">
          {t(($) => $.customerDashboard.spendingBreakdown.totalSpend)}
        </Text>
      </Stack>
      <VSpacer size="4" />
      <Divider />
      {categoryRewards.map((category, index) => (
        <Fragment key={category.id}>
          <VSpacer size="3" />
          <Stack direction="row" justifyContent="flex-start">
            <Box
              bgcolor={ColorIndex[index]}
              borderRadius="3px"
              height="12px"
              margin="6px"
              width="12px"
            />
            <HSpacer size="3" />
            <Stack flexGrow={1} flexShrink={1}>
              <Stack direction="row">
                <Text
                  category="title-small"
                  flexShrink={1}
                  sx={{ wordBreak: 'balanced' }}
                >
                  {category.name}
                </Text>
                <HSpacer size="5" />
                {!shouldHideZeroDollarTotalSpendForCategory(category) && (
                  <Text
                    category="title-small"
                    flexGrow={1}
                    justifyContent="flex-end"
                    textAlign="right"
                  >
                    {formatCurrency(category.totalSpend, i18next)}
                  </Text>
                )}
              </Stack>
              {areProgramPointsFetching ? (
                <Skeleton width="150px" />
              ) : (
                <Text category="body-medium">
                  {t($ => $.customers.details.pointsEarned, { count: category.points })}
                </Text>
              )}
              <VSpacer size="2" />
            </Stack>
          </Stack>
          <VSpacer size="3" />
          <Divider />
          <VSpacer size="3" />
        </Fragment>
      ))}
      {!areProgramPointsFetching && grantedPoints > 0 && (
        <>
          <Stack direction="row" justifyContent="flex-start">
            <Box height="12px" margin="6px" width="12px" />
            <HSpacer size="3" />
            <Stack flexGrow={1} flexShrink={1}>
              <Text category="title-small" sx={{ wordBreak: 'balanced' }}>
                {t($ => $.customerDashboard.spendingBreakdown.grantedPoints)}
              </Text>
              <Text category="body-medium">
                {t($ => $.customers.details.pointsEarned, { count: grantedPoints })}
              </Text>
              <VSpacer size="2" />
            </Stack>
          </Stack>
          <VSpacer size="3" />
          <Divider />
          <VSpacer size="3" />
        </>
      )}
    </Stack>
  );
};
